<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-3xl font-bold text-gray-900">Commission Tracking</h1>
      <div class="flex space-x-3">
        <button 
          @click="manualSync" 
          :disabled="syncLoading"
          class="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg flex items-center"
        >
          <i class="fas fa-sync-alt mr-2" :class="{ 'animate-spin': syncLoading }"></i> 
          {{ syncLoading ? 'Syncing...' : 'Sync Stripe' }}
        </button>
        <a :href="routes.create" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center">
          <i class="fas fa-plus mr-2"></i> Add Manual Transaction
        </a>
      </div>
    </div>

    <!-- Summary Stats -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div class="bg-white rounded-card shadow-xl p-6 border-l-4 border-yellow-400">
        <div class="flex items-center">
          <div class="flex-1">
            <div class="text-sm font-medium text-yellow-600 uppercase tracking-wide">
              Unmapped Commissions
            </div>
            <div class="text-2xl font-bold text-gray-900">{{ stats.total_unmapped }}</div>
            <div class="text-sm text-gray-500">{{ formatCurrency(stats.unmapped_value) }} EUR</div>
          </div>
          <div class="ml-4">
            <i class="fas fa-exclamation-triangle text-3xl text-gray-300"></i>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-card shadow-xl p-6 border-l-4 border-blue-400">
        <div class="flex items-center">
          <div class="flex-1">
            <div class="text-sm font-medium text-blue-600 uppercase tracking-wide">
              Mapped (Unpaid)
            </div>
            <div class="text-2xl font-bold text-gray-900">{{ stats.mapped_unpaid }}</div>
            <div class="text-sm text-gray-500">{{ formatCurrency(stats.mapped_unpaid_value) }} EUR</div>
          </div>
          <div class="ml-4">
            <i class="fas fa-check-circle text-3xl text-gray-300"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-card shadow-xl mb-8">
      <div class="px-6 py-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Filters</h2>
      </div>
      <div class="p-6">
        <form @submit.prevent="applyFilters" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label for="status" class="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select v-model="localFilters.status" id="status" class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="">All</option>
              <option value="unmapped">Unmapped</option>
              <option value="mapped">Mapped</option>
            </select>
          </div>

          <div>
            <label for="company_id" class="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <select v-model="localFilters.company_id" id="company_id" class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="">All Companies</option>
              <option v-for="company in companies" :key="company.id" :value="company.id">
                {{ company.name }}
              </option>
            </select>
          </div>

          <div>
            <label for="from_date" class="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input v-model="localFilters.from_date" type="date" id="from_date" class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
          </div>

          <div>
            <label for="to_date" class="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input v-model="localFilters.to_date" type="date" id="to_date" class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
          </div>

          <div>
            <label for="search" class="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input 
              v-model="localFilters.search" 
              type="text" 
              id="search" 
              class="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" 
              placeholder="Email, name, or Stripe ID"
            >
          </div>

          <div class="flex items-end space-x-2">
            <button type="submit" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">Filter</button>
            <button type="button" @click="clearFilters" class="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg">Clear</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="bg-white rounded-card shadow-xl">
      <div class="px-6 py-4 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">Transactions</h2>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source Commission</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager Commission</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr 
              v-for="(transaction, index) in transactions" 
              :key="transaction.id"
              :class="[
                transaction.auto_mapped ? 'bg-green-50' : '',
                activeRowIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'
              ]"
              @click="setActiveRow(index)"
              class="cursor-pointer"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(transaction.transaction_date) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div class="font-medium">{{ formatAmount(transaction.amount_cents, transaction.currency) }}</div>
                <div class="text-gray-500">Net: {{ formatAmount(transaction.net_amount_cents, transaction.currency) }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <div v-if="transaction.company" class="flex items-center">
                  <a 
                    :href="getCompanyViewUrl(transaction.company.id)"
                    target="_blank"
                    class="mf-link font-medium"
                  >
                    {{ transaction.company.name }}
                  </a>
                  <span v-if="transaction.auto_mapped" class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Auto
                  </span>
                </div>
                <div v-else class="flex items-center space-x-2">
                  <span class="text-red-600 font-medium">Unmapped Company</span>
                  <button 
                    @click="showCompanyMapper(transaction)"
                    class="bg-yellow-100 hover:bg-yellow-200 text-yellow-700 px-2 py-1 rounded text-xs font-medium"
                  >
                    Map Company
                  </button>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <div class="text-gray-900">{{ transaction.customer_email || 'N/A' }}</div>
                <div v-if="transaction.customer_name" class="text-gray-500">{{ transaction.customer_name }}</div>
                <div v-if="transaction.matched_user_id" class="text-green-600 text-xs">Matched User</div>
              </td>
              <td class="px-6 py-4 text-sm text-gray-900">
                <div class="max-w-xs truncate">{{ transaction.product_description || 'N/A' }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <commission-mapping
                  v-if="getSourceMapping(transaction)"
                  :mapping="getSourceMapping(transaction)"
                  :affiliate-companies="affiliateCompanies"
                  :routes="routes"
                  commission-type="source"
                  @mapped="handleMappingChanged"
                />
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <commission-mapping
                  v-if="getManagerMapping(transaction)"
                  :mapping="getManagerMapping(transaction)"
                  :affiliate-companies="affiliateCompanies"
                  :routes="routes"
                  commission-type="account_manager"
                  @mapped="handleMappingChanged"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Pagination -->
      <div class="px-6 py-4 border-t border-gray-200">
        <nav class="flex items-center justify-center space-x-2">
          <button 
            @click="goToPage(pagination.current_page - 1)"
            :disabled="!pagination.prev_page_url"
            :class="[
              'px-3 py-2 rounded-lg text-sm font-medium',
              pagination.prev_page_url 
                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50' 
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            ]"
          >
            Previous
          </button>
          
          <button 
            v-for="page in visiblePages" 
            :key="page"
            @click="goToPage(page)"
            :class="[
              'px-3 py-2 rounded-lg text-sm font-medium',
              page === pagination.current_page
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            ]"
          >
            {{ page }}
          </button>
          
          <button 
            @click="goToPage(pagination.current_page + 1)"
            :disabled="!pagination.next_page_url"
            :class="[
              'px-3 py-2 rounded-lg text-sm font-medium',
              pagination.next_page_url 
                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50' 
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            ]"
          >
            Next
          </button>
        </nav>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import CommissionMapping from './CommissionMapping.vue'

const props = defineProps({
  initialTransactions: Array,
  pagination: Object,
  stats: Object,
  companies: Array,
  affiliateCompanies: Array,
  filters: Object,
  routes: Object
})

// Reactive state
const transactions = ref(props.initialTransactions)
const activeRowIndex = ref(-1)
const localFilters = ref({ ...props.filters })
const syncLoading = ref(false)

// Computed properties

const visiblePages = computed(() => {
  const current = props.pagination.current_page
  const total = props.pagination.last_page
  const delta = 2
  
  const range = []
  const rangeWithDots = []
  
  for (let i = Math.max(2, current - delta); 
       i <= Math.min(total - 1, current + delta); 
       i++) {
    range.push(i)
  }
  
  if (current - delta > 2) {
    rangeWithDots.push(1, '...')
  } else {
    rangeWithDots.push(1)
  }
  
  rangeWithDots.push(...range)
  
  if (current + delta < total - 1) {
    rangeWithDots.push('...', total)
  } else if (total > 1) {
    rangeWithDots.push(total)
  }
  
  return rangeWithDots.filter(page => page !== 1 || total === 1)
})

// Methods
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

const formatAmount = (cents, currency) => {
  const amount = cents / 100
  return `${formatCurrency(amount)} ${currency.toUpperCase()}`
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}


const setActiveRow = (index) => {
  activeRowIndex.value = index
}

const getSourceMapping = (transaction) => {
  return transaction.commission_mappings?.find(m => m.commission_type === 'source')
}

const getManagerMapping = (transaction) => {
  return transaction.commission_mappings?.find(m => m.commission_type === 'account_manager')
}

const handleMappingChanged = () => {
  window.location.reload()
}

const showCompanyMapper = async (transaction) => {
  // Debug: Check if the route is available
  if (!props.routes.mapCompany) {
    console.error('mapCompany route not available. Available routes:', props.routes)
    alert('Company mapping route not available. Check console for details.')
    return
  }
  
  const companyId = prompt(`Enter Company ID to map transaction ${transaction.stripe_id || transaction.id} to:`)
  if (companyId && !isNaN(companyId)) {
    try {
      const response = await axios.post(props.routes.mapCompany, {
        transaction_id: transaction.id,
        company_id: parseInt(companyId)
      })
      
      if (response.data.success) {
        alert(response.data.message)
        window.location.reload()
      } else {
        alert('Failed to map company: ' + response.data.message)
      }
    } catch (error) {
      console.error('Error mapping company:', error)
      if (error.response?.data?.message) {
        alert('Error: ' + error.response.data.message)
      } else {
        alert('Error mapping company to transaction')
      }
    }
  } else if (companyId !== null) {
    alert('Please enter a valid numeric Company ID')
  }
}

const suggestMapping = async (transaction) => {
  try {
    const response = await axios.post(props.routes.suggestMapping, {
      company_id: transaction.company_id
    })
    
    if (response.data.suggestions.source || response.data.suggestions.account_manager) {
      console.log('Suggestions:', response.data.suggestions)
      alert('Suggestions available - this feature needs more implementation')
    }
  } catch (error) {
    console.error('Error getting suggestions:', error)
  }
}


const manualSync = async () => {
  syncLoading.value = true
  
  try {
    const response = await axios.post(props.routes.manualSync)
    
    if (response.data.success) {
      alert(response.data.message)
      // Optionally reload the page after a short delay to show fresh data
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } else {
      alert('Failed to sync: ' + response.data.message)
    }
  } catch (error) {
    console.error('Error syncing:', error)
    alert('Error syncing with Stripe')
  } finally {
    syncLoading.value = false
  }
}

const applyFilters = () => {
  const params = new URLSearchParams()
  
  Object.entries(localFilters.value).forEach(([key, value]) => {
    if (value) {
      params.append(key, value)
    }
  })
  
  window.location.href = `${props.routes.index}?${params.toString()}`
}

const clearFilters = () => {
  window.location.href = props.routes.index
}

const getCompanyViewUrl = (companyId) => {
  return props.routes.companyView.replace(':id', companyId)
}

const goToPage = (page) => {
  const params = new URLSearchParams(window.location.search)
  params.set('page', page)
  window.location.href = `${props.routes.index}?${params.toString()}`
}

// Keyboard navigation
const handleKeyDown = (e) => {
  if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return
  
  switch (e.key) {
    case 'j':
      e.preventDefault()
      if (activeRowIndex.value < transactions.value.length - 1) {
        activeRowIndex.value++
      }
      break
    case 'k':
      e.preventDefault()
      if (activeRowIndex.value > 0) {
        activeRowIndex.value--
      } else if (activeRowIndex.value === -1 && transactions.value.length > 0) {
        activeRowIndex.value = transactions.value.length - 1
      }
      break
    case ' ':
      e.preventDefault()
      // Space key functionality removed (was for bulk selection)
      break
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>